// Reusable Pagination Component
// Place this in your React project's src/components/PaginationComponent.jsx

import React from 'react';

const PaginationComponent = ({
  pagination,
  onPageChange,
  onPerPageChange,
  showPerPageSelector = true,
  maxVisiblePages = 5
}) => {
  const { currentPage, perPage, total, totalPages, from, to } = pagination;

  const generatePageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handlePerPageChange = (newPerPage) => {
    if (onPerPageChange) {
      onPerPageChange(newPerPage);
    }
  };

  if (totalPages <= 1 && !showPerPageSelector) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      {/* Pagination Info */}
      <div className="text-sm text-gray-700">
        {total > 0 ? (
          <>
            Showing <span className="font-medium">{from}</span> to <span className="font-medium">{to}</span> of{' '}
            <span className="font-medium">{total}</span> {total === 1 ? 'item' : 'items'}
          </>
        ) : (
          'No items found'
        )}
      </div>

      {/* Page Controls */}
      {totalPages > 1 && (
        <div className="flex items-center space-x-1">
          <button
            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(1)}
            title="First page"
          >
            ⟪
          </button>

          <button
            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            title="Previous page"
          >
            ⟨
          </button>

          {generatePageNumbers().map(pageNum => (
            <button
              key={pageNum}
              className={`px-3 py-1 text-sm border rounded transition-colors ${
                pageNum === currentPage
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </button>
          ))}

          <button
            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            title="Next page"
          >
            ⟩
          </button>

          <button
            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(totalPages)}
            title="Last page"
          >
            ⟫
          </button>
        </div>
      )}

      {/* Items Per Page Selector */}
      {showPerPageSelector && (
        <div className="flex items-center space-x-2 text-sm">
          <label htmlFor="perPage" className="text-gray-700">
            Items per page:
          </label>
          <select
            id="perPage"
            value={perPage}
            onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default PaginationComponent;